import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, ScrollToTop, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';
import { InternalLinks } from '../components/InternalLinks';

const ServicesPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  return (
    <main className="pt-32" data-testid="services-page">
      <SEOHead
        title="Nos Services | EMS, Boxing, Scanner 3D, Power Plate | Body-Up Nyon"
        description="Services Body-Up Nyon : Personal Training sur mesure, EMS Drysuit XBody, Boxing, Scanner 3D Styku, Power Plate, Hypoxico. Studio privé 150m². Consultation gratuite."
        path="/services"
      />
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">💪 {t.servicesPage.subtitle}</span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-4">{t.servicesPage.title}</h1>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
                {t.servicesPage.description}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimatedSection key={service.id} delay={index * 75}>
                <div 
                  onClick={() => {
                    if (service.id === 'online-coaching') {
                      window.open(BOOKING_URL, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(`/service/${service.id}`);
                    }
                  }}
                  className="group bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-primary/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2 cursor-pointer relative"
                >
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      style={{ objectPosition: service.imagePosition || "center center" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-primary/90 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">{service.emoji}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                    <p className="text-gray-400 mb-4">{service.description}</p>
                    <div className="w-full py-2 px-4 bg-white/10 hover:bg-primary text-white border border-white/20 hover:border-primary rounded-lg text-center group-hover:bg-primary transition-all flex items-center justify-center gap-2">
                      {t.services.learnMore}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage Interne - Blog & Tarifs */}
      <section className="py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <InternalLinks type="blog" />
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              🤔 {t.cta.title}
            </h2>
            <p className="text-white/90 text-lg mb-8">
              {t.cta.description}
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 hover:scale-105 transition-all">
                <Calendar className="w-5 h-5 mr-2" />
                {t.cta.button}
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Individual Service Page

export default ServicesPage;
